import * as React from 'react'
import { Clock } from 'lucide-react'
import { cn } from '../lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Button } from './button'

export interface TimePickerProps {
  value?: string      // "HH:mm" 24-hour
  onChange?: (value: string) => void
  step?: number       // minute granularity, default 5
  placeholder?: string
  disabled?: boolean
  className?: string
  id?: string
  'aria-label'?: string
  error?: boolean
  /**
   * Earliest time that may be chosen, "HH:mm". Anything before it is greyed
   * out, and a scroll that lands on it snaps forward to this.
   *
   * Enforced on commit rather than only on click, because the drum selects on
   * scroll as well: refusing the click alone would leave a past time one flick
   * away, chosen without anything being pressed.
   */
  min?: string
}

/* ─── helpers ──────────────────────────────────────────────────────── */
const pad = (n: number) => String(n).padStart(2, '0')

const parseHM = (v?: string, step = 5) => {
  if (!v) {
    const now = new Date()
    const snap = Math.round(now.getMinutes() / step) * step
    const overflow = snap >= 60
    return {
      h: (now.getHours() + (overflow ? 1 : 0)) % 24,
      m: overflow ? 0 : snap,
    }
  }
  const [hs, ms] = v.split(':').map(Number)
  return {
    h: Number.isFinite(hs) ? Math.min(23, Math.max(0, hs)) : 9,
    m: Number.isFinite(ms) ? Math.min(59, Math.max(0, ms)) : 0,
  }
}

const fmt12 = (h: number, m: number) => {
  const period = h >= 12 ? 'PM' : 'AM'
  const hh = h % 12 === 0 ? 12 : h % 12
  return `${pad(hh)}:${pad(m)} ${period}`
}

/**
 * Hold a time to its floor.
 *
 * The drum selects on scroll as well as on click, so greying the out-of-range
 * items out is not enough on its own: a flick can land on one without anything
 * being pressed. Anything below the floor snaps forward to it instead.
 *
 * Rounded onto the picker's own grid, so the value and the row the drum is
 * showing are the same thing. Capped at the last slot of the day — a floor in
 * the final minutes before midnight has nowhere later to go, and the form's
 * own submit check has the last word there.
 */
export function clampToFloor(time: string, min: string | undefined, step: number): string {
  if (!min) return time
  const [h, m] = time.split(':').map(Number)
  const [mh, mm] = min.split(':').map(Number)
  if (![h, m, mh, mm].every(Number.isFinite)) return time

  const floor = mh * 60 + mm
  if (h * 60 + m >= floor) return time

  const snapped = Math.min(Math.ceil(floor / step) * step, 24 * 60 - step)
  return `${pad(Math.floor(snapped / 60))}:${pad(snapped % 60)}`
}

// 3 visible items — compact drum
const ITEM_H   = 36  // px per row
const VISIBLE  = 3   // rows visible at once  (1 above + selected + 1 below)
const COL_H    = ITEM_H * VISIBLE  // 108 px

/* ─── Drum column ───────────────────────────────────────────────────── */
interface DrumColumnProps {
  items: number[]
  selectedIdx: number
  onSelect: (idx: number) => void
  format: (v: number) => string
  /** Out of range — shown, but not a choice. */
  isDisabled?: (value: number) => boolean
}

function DrumColumn({ items, selectedIdx, onSelect, format, isDisabled }: DrumColumnProps) {
  const ref           = React.useRef<HTMLDivElement>(null)
  const isProgrammatic = React.useRef(false)
  const snapTimer     = React.useRef<ReturnType<typeof setTimeout>>()

  const scrollTo = React.useCallback((idx: number, smooth: boolean) => {
    if (!ref.current) return
    isProgrammatic.current = true
    if (smooth) {
      ref.current.scrollTo({ top: idx * ITEM_H, behavior: 'smooth' })
      setTimeout(() => { isProgrammatic.current = false }, 400)
    } else {
      ref.current.scrollTop = idx * ITEM_H
      isProgrammatic.current = false
    }
  }, [])

  // Instant scroll on mount
  React.useLayoutEffect(() => {
    if (ref.current) ref.current.scrollTop = selectedIdx * ITEM_H
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Smooth scroll when selection changes externally
  const prevIdx = React.useRef(selectedIdx)
  React.useEffect(() => {
    if (prevIdx.current !== selectedIdx) {
      prevIdx.current = selectedIdx
      if (!isProgrammatic.current) scrollTo(selectedIdx, true)
    }
  }, [selectedIdx, scrollTo])

  const handleScroll = () => {
    if (isProgrammatic.current) return
    clearTimeout(snapTimer.current)
    snapTimer.current = setTimeout(() => {
      if (!ref.current || isProgrammatic.current) return
      const idx = Math.min(Math.max(Math.round(ref.current.scrollTop / ITEM_H), 0), items.length - 1)
      onSelect(idx)
      scrollTo(idx, true)
    }, 160)
  }

  return (
    <div className="relative w-[52px]">
      {/* Center highlight — 1 ITEM_H from top */}
      <div
        className="absolute left-1 right-1 bg-primary/10 dark:bg-primary/15 rounded-lg border border-primary/20 pointer-events-none z-20"
        style={{ top: ITEM_H, height: ITEM_H }}
      />
      {/* Top fade */}
      <div
        className="absolute inset-x-0 top-0 bg-gradient-to-b from-card to-transparent pointer-events-none z-10"
        style={{ height: ITEM_H }}
      />
      {/* Bottom fade */}
      <div
        className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-card to-transparent pointer-events-none z-10"
        style={{ height: ITEM_H }}
      />

      <div
        ref={ref}
        className="overflow-y-auto [&::-webkit-scrollbar]:hidden"
        style={{ height: COL_H, scrollbarWidth: 'none' }}
        onScroll={handleScroll}
      >
        {/* 1 spacer item above so first item can center */}
        <div style={{ height: ITEM_H }} />
        {items.map((v, i) => {
          const sel  = i === selectedIdx
          const near = Math.abs(i - selectedIdx) === 1
          const off  = isDisabled?.(v) ?? false
          return (
            <div
              key={v}
              style={{ height: ITEM_H }}
              onClick={() => { if (off) return; onSelect(i); scrollTo(i, true) }}
              className={cn(
                'flex items-center justify-center select-none transition-all duration-100 relative z-30',
                off ? 'cursor-not-allowed line-through opacity-30' : 'cursor-pointer',
                sel  ? 'text-primary font-bold text-lg'    :
                near ? 'text-foreground/50 font-medium text-sm' :
                       'text-foreground/20 font-normal text-xs',
              )}
            >
              {format(v)}
            </div>
          )
        })}
        {/* 1 spacer item below so last item can center */}
        <div style={{ height: ITEM_H }} />
      </div>
    </div>
  )
}

/* ─── Main component ────────────────────────────────────────────────── */
export function TimePicker({
  value,
  onChange,
  step = 5,
  placeholder = 'Select time',
  disabled,
  className,
  id,
  'aria-label': ariaLabel,
  error,
  min,
}: TimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const { h, m } = parseHM(value, step)

  const isPM = h >= 12
  const h12  = h % 12 === 0 ? 12 : h % 12

  const hours = React.useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), [])
  const mins  = React.useMemo(
    () => Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step),
    [step],
  )

  const hourIdx = hours.indexOf(h12)
  const minIdx  = React.useMemo(() => {
    const snapped = Math.min(Math.round(m / step) * step, (mins.length - 1) * step)
    const i = mins.indexOf(snapped)
    return i >= 0 ? i : 0
  }, [m, step, mins])

  const displayM = mins[minIdx] ?? m

  /** The floor, in minutes since midnight, or null when there is none. */
  const floor = React.useMemo(() => {
    if (!min) return null
    const [mh, mm] = min.split(':').map(Number)
    if (!Number.isFinite(mh) || !Number.isFinite(mm)) return null
    return mh * 60 + mm
  }, [min])

  const h24Of = (h12v: number, pm: boolean) => (h12v % 12) + (pm ? 12 : 0)
  const tooEarly = (h12v: number, mv: number, pm: boolean) =>
    floor != null && h24Of(h12v, pm) * 60 + mv < floor

  const commit = (newH12: number, newM: number, newIsPM: boolean) => {
    const h24 = h24Of(newH12, newIsPM)
    onChange?.(clampToFloor(`${pad(h24)}:${pad(newM)}`, min, step))
  }

  // A half of the day entirely behind the floor is not a choice either.
  const amTooEarly = floor != null && 11 * 60 + 59 < floor
  const pmTooEarly = floor != null && 23 * 60 + 59 < floor

  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            'h-10 w-full justify-start gap-2 px-3 text-sm font-normal',
            !value && 'text-muted-foreground',
            error && 'border-red-500 focus-visible:ring-red-500/30',
            className,
          )}
        >
          <Clock className="w-4 h-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{value ? fmt12(h, m) : placeholder}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={6}
        collisionPadding={12}
        className="w-[180px] p-0 overflow-hidden rounded-2xl shadow-xl border-border"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* ── Header: AM / PM only ── */}
        <div className="flex items-center justify-center px-3 py-2 border-b border-border">
          <div className="flex rounded-lg overflow-hidden border border-border text-xs font-bold shadow-sm">
            <button
              type="button"
              onClick={() => { if (!amTooEarly) commit(h12, displayM, false) }}
              disabled={amTooEarly}
              className={cn(
                'px-3 py-1 transition-colors',
                amTooEarly && 'cursor-not-allowed opacity-40',
                !isPM ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted',
              )}
            >AM</button>
            <button
              type="button"
              onClick={() => { if (!pmTooEarly) commit(h12, displayM, true) }}
              disabled={pmTooEarly}
              className={cn(
                'px-3 py-1 transition-colors border-l border-border',
                pmTooEarly && 'cursor-not-allowed opacity-40',
                isPM ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted',
              )}
            >PM</button>
          </div>
        </div>

        {/* ── Drum columns ── */}
        <div className="flex items-center justify-center gap-1 px-3 pt-2 pb-1">
          {/* Hour label */}
          <div className="flex flex-col items-center w-[52px]">
            <p className="text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-widest mb-1">Hour</p>
            <DrumColumn
              items={hours}
              selectedIdx={hourIdx >= 0 ? hourIdx : 0}
              onSelect={(i) => commit(hours[i], displayM, isPM)}
              // An hour is out only when its LAST minute is still too early —
              // the hour the floor falls inside is half available.
              isDisabled={(hv) => tooEarly(hv, 59, isPM)}
              format={pad}
            />
          </div>

          <span className="text-lg font-bold text-muted-foreground/30 mb-0 pb-0 mt-4">:</span>

          {/* Minute label */}
          <div className="flex flex-col items-center w-[52px]">
            <p className="text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-widest mb-1">Min</p>
            <DrumColumn
              items={mins}
              selectedIdx={minIdx}
              onSelect={(i) => commit(h12, mins[i], isPM)}
              isDisabled={(mv) => tooEarly(h12, mv, isPM)}
              format={pad}
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs h-7 gap-1 text-muted-foreground hover:text-foreground px-2"
            onClick={() => {
              const now = new Date()
              const snap = Math.round(now.getMinutes() / step) * step
              const overflow = snap >= 60
              const h24 = (now.getHours() + (overflow ? 1 : 0)) % 24
              const mm = overflow ? 0 : snap
              // Through `commit`, so "Now" obeys the floor too: rounding to
              // the nearest slot can land a minute or two behind it.
              commit(h24 % 12 === 0 ? 12 : h24 % 12, mm, h24 >= 12)
            }}
          >
            <Clock className="w-3 h-3" />
            Now
          </Button>
          <Button
            type="button"
            size="sm"
            className="text-xs h-7 px-5"
            onClick={() => setOpen(false)}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
